import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  InputBase,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { api } from './api';
import {
  amount,
  decimalInput,
  money,
  AccessoryPanel,
  type LineAccessory,
  plain,
  PRICE_DECIMALS,
  QTY_DECIMALS,
  skuPicker,
} from './ui';
import { adjustReasons, blendedRate, canUseSku, signedPercent } from '@zilian/contracts';
import Add from '@mui/icons-material/Add';
import AddCircleOutline from '@mui/icons-material/AddCircleOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CorporateFareOutlined from '@mui/icons-material/CorporateFareOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutlineOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import PriceCheckOutlined from '@mui/icons-material/PriceCheckOutlined';
import Remove from '@mui/icons-material/Remove';
import VerifiedOutlined from '@mui/icons-material/VerifiedOutlined';
import SkuCreateDialog, { type CreatedSku } from './SkuCreateDialog';
import { FieldLabel, Note, ON_SURFACE, Section, SURFACE } from './FormParts';

interface Party {
  id: string;
  name: string;
  /** A linked enterprise on the platform, or this enterprise's record of one that is not. */
  kind: 'partner' | 'record';
  hint?: string;
}
interface Props {
  side: 'BUY' | 'SELL';
  workspace: string;
  canCreateSku: boolean;
  bootstrap: any;
  onClose: (catalogChanged: boolean) => void;
  onCreated: (id?: string) => void;
}

// Figures read in a monospaced face so amounts line up; cards sit on a light surface.
const MONO = '"SF Mono", Menlo, Consolas, "Liberation Mono", monospace';
/** Product, quantity, price, amount: the same columns for the header and every line. */
/** The height and outline of a small outlined field, so the stepper lines up with its row. */
const FIELD_HEIGHT = 40;
const LINE_GRID = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: '5fr 3fr 2fr 2fr' },
  gap: 1.5,
  alignItems: 'center',
} as const;

/** A quantity with − and + steps of one, still typable; the unit reads beside the figure. */
function Stepper(props: {
  value: string;
  unit: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const step = (by: number) =>
    props.onChange(
      String(Math.max(0, Number((Number(props.value || 0) + by).toFixed(QTY_DECIMALS)))),
    );
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
        maxWidth: 170,
        height: FIELD_HEIGHT,
        mx: 'auto',
        bgcolor: 'background.paper',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: 1,
        overflow: 'hidden',
        '&:hover': props.disabled ? {} : { borderColor: 'text.primary' },
        '&:focus-within': { borderColor: 'primary.main', boxShadow: '0 0 0 1px #17665f' },
        ...(props.disabled ? { borderColor: 'rgba(0, 0, 0, 0.12)' } : {}),
      }}
    >
      <IconButton
        size="small"
        aria-label="减少数量"
        disabled={props.disabled || !(Number(props.value) > 1)}
        onClick={() => step(-1)}
        sx={{ borderRadius: 0, px: 1.25 }}
      >
        <Remove sx={{ fontSize: 16 }} />
      </IconButton>
      <InputBase
        value={props.value}
        placeholder="0"
        disabled={props.disabled}
        onChange={(event) => props.onChange(decimalInput(event.target.value, QTY_DECIMALS))}
        inputProps={{ inputMode: 'decimal', 'aria-label': '数量', style: { textAlign: 'center' } }}
        sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600 }}
      />
      {props.unit && (
        <Typography
          color="text.secondary"
          sx={{ fontSize: 12, flexShrink: 0, alignSelf: 'center' }}
        >
          {props.unit}
        </Typography>
      )}
      <IconButton
        size="small"
        aria-label="增加数量"
        disabled={props.disabled}
        onClick={() => step(1)}
        sx={{ borderRadius: 0, px: 1.25 }}
      >
        <Add sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

interface Line {
  key: string;
  skuId: string;
  quantity: string;
  unitPrice: string;
  /** Where a price filled in for the user came from, until it is edited. */
  source?: string;
}
const blankLine = (): Line => ({
  key: crypto.randomUUID(),
  skuId: '',
  quantity: '',
  unitPrice: '',
});
// Own items carry it as salesPrice (bootstrap, just created), catalogs as sales_price.
const quoteOf = (item: any): string | null => item?.sales_price ?? item?.salesPrice ?? null;
type LastPrices = Record<string, { unitPrice: string; date: string }>;
const monthDay = (date: string) => `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`;

export default function TradeOrderDialog(p: Props) {
  const parties: Party[] = useMemo(
    () =>
      p.bootstrap.partners
        .filter((partner: any) => partner.status === 'ACTIVE')
        .map((partner: any): Party => ({
          id: partner.inviter_id === p.workspace ? partner.invitee_id : partner.inviter_id,
          name: partner.partner_name,
          kind: 'partner',
        })),
    [p.bootstrap, p.workspace],
  );
  // Counterparties not on the platform, for self-recorded orders.
  const [records, setRecords] = useState<Party[]>([]);
  useEffect(() => {
    api<any[]>('/counterparties')
      .then((rows) =>
        setRecords(
          rows
            // One that has joined is ordered as a linked enterprise instead.
            .filter((r) => !r.partner_workspace_id)
            .map((r) => {
              const owed = Number(p.side === 'SELL' ? r.receivable : r.payable);
              return {
                id: r.id,
                name: r.name,
                kind: 'record' as const,
                hint:
                  owed > 0
                    ? `未入驻 · ${p.side === 'SELL' ? '应收' : '应付'} ${money(owed)}`
                    : '未入驻单位',
              };
            }),
        ),
      )
      .catch(() => undefined);
  }, [p.side]);
  const options = useMemo(() => [...parties, ...records], [parties, records]);
  const [party, setParty] = useState<Party | string | null>(null);
  const linked = typeof party === 'object' && party?.kind === 'partner';
  const record = typeof party === 'object' && party?.kind === 'record' ? party : null;
  const partyId = linked ? party.id : '';
  const partyName = typeof party === 'object' && party ? party.name : (party ?? '');
  const typedExisting =
    !record && typeof party === 'string' ? records.find((r) => r.name === party.trim()) : undefined;
  // A new customer's number, registered with it, so the printed code can be signed.
  const newCustomer =
    p.side === 'SELL' && !linked && !record && !typedExisting && !!String(partyName).trim();
  const [receiverPhone, setReceiverPhone] = useState('');
  const [remoteCatalog, setRemoteCatalog] = useState<any[]>([]);
  const [createdSkus, setCreatedSkus] = useState<CreatedSku[]>([]);
  const [creatingSku, setCreatingSku] = useState(false);
  const ownCatalog = useMemo(
    () => [...p.bootstrap.skus, ...createdSkus].filter((item) => canUseSku(item.usage, p.side)),
    [p.bootstrap.skus, createdSkus, p.side],
  );
  const catalog = linked ? remoteCatalog : ownCatalog;
  const [lines, setLines] = useState<Line[]>([blankLine()]);
  const [deliveryDate, setDeliveryDate] = useState(new Date().toLocaleDateString('sv-SE'));
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [operationKey] = useState(() => crypto.randomUUID());
  const lockedPrice = linked && p.side === 'BUY';
  // What was last paid to this off-platform supplier for each item.
  const supplierId = p.side === 'BUY' ? (record?.id ?? typedExisting?.id ?? '') : '';
  const [lastPrices, setLastPrices] = useState<LastPrices>({});
  const ownSelection = !lockedPrice;
  const createLabel = p.side === 'BUY' ? '新建采购物料' : '新建销售产品';

  useEffect(() => {
    let active = true;
    setError('');
    setRemoteCatalog([]);
    // Switching counterparty changes the catalog, so items chosen for the old one no longer apply.
    setLines([blankLine()]);
    if (!linked) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api<any[]>(`/catalog/${p.side === 'BUY' ? partyId : p.workspace}`)
      .then((items) => {
        if (active) setRemoteCatalog(items);
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [partyId, linked, p.workspace, p.side]);

  const skuOf = (line: Line) => catalog.find((item) => item.id === line.skuId);
  const priceOf = (line: Line) => (lockedPrice ? (skuOf(line)?.sales_price ?? '') : line.unitPrice);
  const lineTotal = (line: Line) => Number(line.quantity) * Number(priceOf(line));
  /**
   * The material adjustment a line carries: sales at the supplier's quote follow its material;
   * a price agreed by hand replaces the quote and is not adjusted. The server decides the same.
   */
  const adjustOf = (line: Line) => {
    const sku = skuOf(line);
    if (!sku?.adjust || (p.side === 'BUY' && !lockedPrice)) return null;
    return Number(priceOf(line)) === Number(quoteOf(sku)) ? sku.adjust : null;
  };
  const update = (key: string, patch: Partial<Line>) =>
    setLines((all) => all.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  /**
   * Picks an item and starts its price: a sale from the item's 销售报价; a purchase from an
   * off-platform supplier from the last price paid to it, else the item's 参考采购价. A price
   * typed by hand is kept, and one filled in for the previous item is replaced.
   */
  const pick = (line: Line, item: any, last = lastPrices): Line => {
    const next = { ...line, skuId: item?.id ?? '' };
    if (lockedPrice || (line.unitPrice && !line.source)) return next;
    const paid = item && last[item.id];
    const [price, source] =
      p.side === 'SELL'
        ? [quoteOf(item), '销售报价']
        : paid
          ? [paid.unitPrice, `上次成交价（${monthDay(paid.date)}）`]
          : [item?.purchasePrice ?? null, '参考采购价'];
    return price === null || price === undefined
      ? { ...next, unitPrice: '', source: undefined }
      : { ...next, unitPrice: plain(price, PRICE_DECIMALS), source };
  };
  useEffect(() => {
    let active = true;
    const load = supplierId
      ? api<{ skuId: string; unitPrice: string; date: string }[]>(
          `/local-orders/last-prices?counterpartyId=${supplierId}`,
        ).then((rows) => Object.fromEntries(rows.map(({ skuId, ...r }) => [skuId, r])))
      : Promise.resolve({} as LastPrices);
    load
      .catch(() => ({}) as LastPrices)
      .then((last) => {
        if (!active) return;
        setLastPrices(last);
        // Prices filled in for another supplier follow the one chosen now.
        setLines((all) =>
          all.map((line) =>
            line.skuId && (!line.unitPrice || line.source)
              ? pick(
                  { ...line, unitPrice: '', source: undefined },
                  ownCatalog.find((item) => item.id === line.skuId),
                  last,
                )
              : line,
          ),
        );
      });
    return () => {
      active = false;
    };
  }, [supplierId]);
  const remove = (key: string) =>
    setLines((all) => (all.length > 1 ? all.filter((line) => line.key !== key) : [blankLine()]));
  const chosen = lines.filter((line) => line.skuId);
  /**
   * What goes out with a product, priced into it (caps with bottles). A supplier's catalog
   * lists it; for our own sales without a linked catalog it comes from the item records.
   */
  const accessoriesOf = (sku: any): LineAccessory[] => {
    if (p.side !== 'SELL' && !lockedPrice) return [];
    if (linked) return sku.bundle ?? [];
    return (p.bootstrap.recipes ?? [])
      .filter((r: any) => r.product_id === sku.id && r.kind === 'BUNDLE')
      .map((r: any) => {
        const item = p.bootstrap.skus.find((s: any) => s.id === r.material_id);
        return (
          item && {
            skuId: item.id,
            name: item.name,
            spec: item.spec,
            unit: item.unit,
            integer: item.integerUnit,
            perPiece: r.ratio,
          }
        );
      })
      .filter(Boolean);
  };
  const goods = chosen.reduce((sum, line) => sum + (lineTotal(line) || 0), 0);
  const adjustment = chosen.reduce(
    (sum, line) => sum + (lineTotal(line) || 0) * Number(adjustOf(line)?.rate ?? 0),
    0,
  );
  const total = goods + adjustment;
  const totalText = total > 0 ? `${money(total)}` : '—';
  const reasons = adjustReasons(chosen.map(adjustOf));

  const close = () => p.onClose(createdSkus.length > 0);
  const lineIssue = chosen
    .map((line) => {
      const sku = skuOf(line);
      if (!sku || !lockedPrice) return '';
      if (sku.sales_price == null) return `${sku.name} 尚未报价，请联系供应商维护报价。`;
      if (sku.mapping_ready === false)
        return `${sku.name} 没有可自动对应的同名采购物料（需名称和单位一致），请先在“往来单位”中设置到货入库的我方商品，或新建同名采购物料。`;
      return '';
    })
    .find(Boolean);
  const block =
    !loading && !catalog.length
      ? lockedPrice
        ? '供应商暂无可售商品，请联系对方维护销售产品和报价。'
        : `暂无${p.side === 'BUY' ? '可采购物料' : '可销售产品'}，可点击右上方新建，或在“商品档案”中调整用途。`
      : (lineIssue ?? '');

  async function submit() {
    if (busy || loading || block) return;
    const valid = chosen.every(
      (line) =>
        Number(line.quantity) > 0 &&
        priceOf(line) !== '' &&
        Number.isFinite(Number(priceOf(line))) &&
        Number(priceOf(line)) >= 0,
    );
    if (!String(partyName).trim() || !chosen.length || !valid || !deliveryDate) {
      setError('请填写往来单位、交付日期，以及每个商品的有效数量和单价');
      return;
    }
    if (newCustomer && receiverPhone.trim() && !/^1\d{10}$/.test(receiverPhone.trim())) {
      setError('对接手机号是 11 位数字');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (linked) {
        const order = await api('/orders', {
          sellerId: p.side === 'SELL' ? p.workspace : partyId,
          buyerId: p.side === 'BUY' ? p.workspace : partyId,
          // Accessories are added by the server from the seller's product records.
          lines: chosen.map((line) => ({
            sellerSkuId: line.skuId,
            quantity: line.quantity,
            unitPrice: priceOf(line),
            adjustRate: adjustOf(line)?.rate ?? '0',
          })),
          deliveryDate,
          note,
          key: operationKey,
        });
        p.onCreated(order.id);
      } else {
        await api('/local-orders', {
          kind: p.side === 'BUY' ? 'PURCHASE' : 'SALE',
          ...(record ? { counterpartyId: record.id } : { counterparty: String(partyName).trim() }),
          ...(newCustomer ? { receiverPhone: receiverPhone.trim() } : {}),
          lines: chosen.map((line) => ({
            skuId: line.skuId,
            quantity: line.quantity,
            unitPrice: line.unitPrice,
            adjustRate: adjustOf(line)?.rate ?? '0',
          })),
          deliveryDate,
          note,
          key: operationKey,
        });
        p.onCreated();
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function skuCreated(next: CreatedSku) {
    setCreatedSkus((items) => [...items, next]);
    if (linked)
      setRemoteCatalog((items) => [
        ...items,
        { ...next, sales_price: next.salesPrice, pack_size: next.packSize },
      ]);
    // Fill the first empty row, or add one, with the item just created.
    setLines((all) => {
      const empty = all.find((line) => !line.skuId);
      return empty
        ? all.map((line) => (line === empty ? pick(line, next) : line))
        : [...all, pick(blankLine(), next)];
    });
    setCreatingSku(false);
    setError('');
  }

  const priceHint = lockedPrice
    ? '单价为供应商报价，如需议价请联系对方修改订单。'
    : p.side === 'SELL'
      ? catalog.some((item) => item.adjust)
        ? '默认带出销售报价；改过单价的商品不做原料调整。'
        : '默认带出销售报价，可按本次实际约定修改。'
      : '默认带出上次成交价或参考采购价，可按实际约定修改。';
  const modeHint = linked
    ? '已关联企业 · 自动读取商品与报价，提交即代表我方确认，对方确认后履约'
    : record || typedExisting
      ? '未入驻单位 · 按实际约定登记，由本企业完成收发货、对账和收付款'
      : String(partyName).trim()
        ? `将新建往来单位「${String(partyName).trim()}」· 由本企业登记收发货和收付款`
        : '选择已关联企业或往来单位，也可直接输入新的单位名称';
  const unitLabel = (line: Line) => skuOf(line)?.unit ?? '';
  const partyTag = linked
    ? { label: '已关联企业', tone: 'success' as const }
    : record || typedExisting
      ? { label: '未入驻单位', tone: 'default' as const }
      : String(partyName).trim()
        ? { label: '新建往来单位', tone: 'default' as const }
        : null;
  const goodsLabel = p.side === 'BUY' ? (linked ? '供应商商品' : '采购物料') : '销售产品';

  return (
    <>
      <Dialog
        open
        fullWidth
        maxWidth="lg"
        onClose={() => !busy && close()}
        slotProps={{ paper: { sx: { maxWidth: 1024 } } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3.5,
            py: 2.25,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography component="span" sx={{ fontSize: 19, fontWeight: 700 }}>
            {p.side === 'BUY' ? '新建采购订单' : '新建销售订单'}
          </Typography>
          <IconButton aria-label="关闭" disabled={busy} onClick={close} sx={{ mr: -1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: SURFACE, px: 3.5, py: '24px !important' }}>
          <Stack spacing={2.5}>
            {(error || block) && (
              <Stack spacing={1}>
                {error && <Alert severity="error">{error}</Alert>}
                {block && <Alert severity="warning">{block}</Alert>}
              </Stack>
            )}

            <Section
              icon={<BusinessOutlined />}
              title={p.side === 'BUY' ? '供应商' : '客户'}
              required
              action={
                partyTag && (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={partyTag.tone}
                    icon={partyTag.tone === 'success' ? <VerifiedOutlined /> : undefined}
                    label={partyTag.label}
                  />
                )
              }
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Autocomplete<Party, false, false, true>
                  sx={{ flex: 1, minWidth: 0 }}
                  freeSolo
                  value={party}
                  options={options}
                  disabled={busy}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                  isOptionEqualToValue={(a, b) => typeof b !== 'string' && a.id === b.id}
                  groupBy={(option) => (option.kind === 'partner' ? '联仓合作企业' : '未入驻单位')}
                  onChange={(_, next) => setParty(next)}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input' || reason === 'clear') setParty(value);
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                      <Typography
                        component="span"
                        color="text.secondary"
                        sx={{ ml: 1, fontSize: 12 }}
                      >
                        {option.kind === 'partner' ? '已关联企业' : option.hint}
                      </Typography>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={p.side === 'BUY' ? '搜索或输入供应商名称' : '搜索或输入客户名称'}
                      slotProps={{
                        ...params.slotProps,
                        input: {
                          ...params.slotProps.input,
                          startAdornment: (
                            <InputAdornment position="start" sx={{ color: 'text.disabled' }}>
                              <CorporateFareOutlined fontSize="small" />
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  )}
                />
                {newCustomer && (
                  <TextField
                    placeholder="对接手机号（选填）"
                    value={receiverPhone}
                    disabled={busy}
                    onChange={(e) =>
                      setReceiverPhone(e.target.value.replace(/\D/g, '').slice(0, 11))
                    }
                    slotProps={{ htmlInput: { inputMode: 'numeric', autoComplete: 'off' } }}
                    sx={{ width: 200, flexShrink: 0 }}
                  />
                )}
              </Box>
              <Note icon={<InfoOutlined />} sx={{ mt: 1.5 }}>
                {modeHint}
                {newCustomer && '；对接手机号用于签收、确认对账单时核验'}
              </Note>
            </Section>

            <Section
              icon={<Inventory2Outlined />}
              title="商品明细"
              extra={
                chosen.length > 0 && (
                  <Typography color="text.secondary" sx={{ fontSize: 12.5, ml: 0.5 }}>
                    共{' '}
                    <Box component="b" sx={{ color: 'primary.main' }}>
                      {chosen.length}
                    </Box>{' '}
                    种商品
                  </Typography>
                )
              }
              action={
                ownSelection &&
                (p.canCreateSku ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddCircleOutline />}
                    disabled={busy || loading}
                    onClick={() => setCreatingSku(true)}
                  >
                    {createLabel}
                  </Button>
                ) : (
                  <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                    缺少档案请联系管理员或仓管新建
                  </Typography>
                ))
              }
            >
              <Box
                sx={{
                  ...LINE_GRID,
                  display: { xs: 'none', sm: 'grid' },
                  px: 1.75,
                  py: 0.75,
                  mb: 1.5,
                  bgcolor: SURFACE,
                  borderRadius: 1,
                  '& p': { fontSize: 12, fontWeight: 600, color: 'text.secondary' },
                }}
              >
                <Typography>{goodsLabel} / 规格配套</Typography>
                <Typography sx={{ textAlign: 'center' }}>数量</Typography>
                <Typography sx={{ textAlign: 'right' }}>
                  {lockedPrice ? '供应商报价' : '约定单价'}（元）
                </Typography>
                <Typography sx={{ textAlign: 'right', pr: 5 }}>金额（小计）</Typography>
              </Box>
              <Stack spacing={1.5}>
                {lines.map((line) => {
                  const sku = skuOf(line);
                  const sum = lineTotal(line);
                  const target = lockedPrice && sku?.mapping;
                  const adjust = adjustOf(line);
                  const accessories = sku ? accessoriesOf(sku) : [];
                  return (
                    <Box
                      key={line.key}
                      sx={{
                        bgcolor: SURFACE,
                        borderRadius: 1,
                        p: 1.75,
                        transition: 'background-color .15s',
                        '&:hover': { bgcolor: '#f1f5f9' },
                      }}
                    >
                      <Box sx={LINE_GRID}>
                        <Autocomplete<any>
                          size="small"
                          value={sku ?? null}
                          options={catalog}
                          disabled={busy || loading || !catalog.length}
                          {...skuPicker({
                            extra: (item) =>
                              lockedPrice
                                ? item.mapping
                                  ? ` · 入库为 ${item.mapping.buyer_name}${item.mapping.auto ? '（同名）' : ''}`
                                  : ' · 未设置对应'
                                : '',
                          })}
                          getOptionDisabled={(item) =>
                            lines.some((l) => l.key !== line.key && l.skuId === item.id)
                          }
                          onChange={(_, next) =>
                            setLines((all) =>
                              all.map((l) => (l.key === line.key ? pick(l, next) : l)),
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={loading ? '正在读取商品…' : '搜索名称、规格或编码'}
                              sx={ON_SURFACE}
                            />
                          )}
                        />
                        <Stepper
                          value={line.quantity}
                          unit={unitLabel(line)}
                          disabled={busy || !line.skuId}
                          onChange={(quantity) => update(line.key, { quantity })}
                        />
                        {lockedPrice ? (
                          <Typography sx={{ textAlign: 'right', fontSize: 14, fontFamily: MONO }}>
                            {priceOf(line) === ''
                              ? '—'
                              : `¥ ${plain(priceOf(line), PRICE_DECIMALS)}`}
                          </Typography>
                        ) : (
                          <TextField
                            size="small"
                            placeholder="0.00"
                            value={line.unitPrice}
                            disabled={busy || loading}
                            onChange={(event) =>
                              update(line.key, {
                                unitPrice: decimalInput(event.target.value, PRICE_DECIMALS),
                                source: undefined,
                              })
                            }
                            sx={ON_SURFACE}
                            slotProps={{
                              htmlInput: {
                                inputMode: 'decimal',
                                'aria-label': '约定单价',
                                style: { textAlign: 'right' },
                              },
                              input: {
                                startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                              },
                            }}
                          />
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: MONO,
                              fontWeight: 700,
                              fontSize: 14,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {sum > 0 ? money(sum) : '—'}
                          </Typography>
                          <IconButton
                            size="small"
                            aria-label="删除这一行"
                            disabled={busy || (lines.length === 1 && !line.skuId)}
                            onClick={() => remove(line.key)}
                            sx={{
                              '&:hover': { color: 'error.main', bgcolor: 'rgba(225,29,72,.06)' },
                            }}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      {(target || adjust || (p.side === 'BUY' && line.source)) && (
                        <Typography color="text.secondary" sx={{ fontSize: 12, mt: 1, px: 0.5 }}>
                          {target &&
                            `到货入库为我方「${target.buyer_name}」${target.auto ? '（同名采购物料，自动对应）' : ''}`}
                          {p.side === 'BUY' && line.source && `单价取自${line.source}`}
                          {target && adjust && ' · '}
                          {adjust &&
                            `原料调整 ${signedPercent(adjust.rate)}（${adjust.material} 较报价时 ${signedPercent(adjust.change)}）`}
                        </Typography>
                      )}
                      {accessories.length > 0 && (
                        <Box
                          sx={{ mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}
                        >
                          <AccessoryPanel
                            accessories={accessories}
                            productUnit={sku!.unit}
                            pieces={line.quantity}
                            defaultOpen
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Add />}
                  disabled={
                    busy ||
                    loading ||
                    lines.length >= 30 ||
                    lines.length >= catalog.length ||
                    lines.some((line) => !line.skuId)
                  }
                  onClick={() => setLines((all) => [...all, blankLine()])}
                  sx={{ borderStyle: 'dashed', '&:hover': { borderStyle: 'dashed' } }}
                >
                  添加商品
                </Button>
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  <PriceCheckOutlined sx={{ fontSize: 15 }} />
                  {priceHint}
                </Typography>
              </Box>
            </Section>

            <Section icon={<LocalShippingOutlined />} title="交付">
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' },
                  gap: 2,
                }}
              >
                <Box>
                  <FieldLabel required>约定交付日期</FieldLabel>
                  <TextField
                    fullWidth
                    type="date"
                    value={deliveryDate}
                    disabled={busy}
                    onChange={(event) => setDeliveryDate(event.target.value)}
                  />
                </Box>
                <Box>
                  <FieldLabel hint="选填">订单备注</FieldLabel>
                  <TextField
                    fullWidth
                    placeholder="收货地址、包装要求、指定物流等"
                    value={note}
                    disabled={busy}
                    onChange={(event) => setNote(event.target.value)}
                  />
                </Box>
              </Box>
            </Section>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{ px: 3.5, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1.5 }}
        >
          <Box sx={{ mr: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
              <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>
                合计金额
                {chosen.length > 0 && (
                  <>
                    {' · '}
                    <Box component="b" sx={{ color: 'text.primary' }}>
                      {chosen.length}
                    </Box>{' '}
                    种商品
                  </>
                )}
              </Typography>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 24,
                  fontWeight: 800,
                  color: 'primary.main',
                  letterSpacing: '-0.02em',
                }}
              >
                {totalText}
              </Typography>
            </Box>
            {adjustment !== 0 && goods > 0 && (
              <Tooltip
                arrow
                placement="top-start"
                title={
                  <Box sx={{ fontSize: 12, lineHeight: 1.7 }}>
                    {reasons.map((r) => (
                      <div key={r}>{r}</div>
                    ))}
                  </Box>
                }
              >
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: 12, cursor: 'help', textDecoration: 'underline dotted' }}
                >
                  货款 {money(goods)} · 原料调整 {adjustment > 0 ? '+' : '-'}
                  {money(Math.abs(adjustment))}（{blendedRate(goods, adjustment)}）
                </Typography>
              </Tooltip>
            )}
          </Box>
          <Button color="inherit" disabled={busy} onClick={close} sx={{ color: 'text.secondary' }}>
            取消
          </Button>
          <Button
            variant="contained"
            disabled={busy || loading || !!block}
            onClick={submit}
            sx={{ px: 3 }}
          >
            {busy ? '正在提交…' : linked ? '提交并确认' : '保存单据'}
          </Button>
        </DialogActions>
      </Dialog>
      {creatingSku && (
        <SkuCreateDialog
          title={createLabel}
          defaultUsage={p.side === 'BUY' ? 'PURCHASE' : 'SALE'}
          allowedUsages={p.side === 'BUY' ? ['PURCHASE', 'BOTH'] : ['SALE', 'BOTH']}
          categories={p.bootstrap.categories}
          onClose={() => setCreatingSku(false)}
          onCreated={skuCreated}
        />
      )}
    </>
  );
}
